/**
 * Editor for per-contract custom sections ("Egna sektioner", ADR-0060).
 *
 * Structure mirrors the data model:
 *   Section (title, placement "efter §X", ordered sub-sections)
 *     SubSection (optional name → numbered §N.M; empty → blends in)
 *       Component (key→value rows OR free text), reorderable
 *
 * Placement is routed by the declared section `ref` ("efter" dropdown) and
 * persists as `afterRef`. The whole list is patched via `onChange`, which the
 * editor wires to the normal dirty-key/update_draft path. The rendered § /
 * §N.M numbers are owned by the numbering engine; we display them from the
 * computed `numbering` map (section) and a local count (sub-section).
 */

import {
  MAX_CUSTOM_COMPONENTS,
  MAX_CUSTOM_ROWS,
  MAX_CUSTOM_SECTIONS,
  MAX_CUSTOM_SUBSECTIONS,
  customSectionAnchorRef,
  type CustomComponent,
  type CustomSection,
  type CustomSubSection,
  type SectionNumbering,
} from '@hyranai/regta-templates';

function newId(): string {
  // `crypto.randomUUID` needs a secure context; the dev frontend is served
  // over plain HTTP, so fall back to a local id (same pattern as PartiesPanel).
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `local-${Math.random().toString(36).slice(2)}-${Date.now()}`;
}

function move<T>(arr: ReadonlyArray<T>, i: number, dir: -1 | 1): T[] {
  const j = i + dir;
  if (i < 0 || j < 0 || j >= arr.length) return arr.slice();
  const next = arr.slice();
  [next[i], next[j]] = [next[j]!, next[i]!];
  return next;
}

const inputCls =
  'w-full rounded border border-stone-300 px-2 py-1 text-sm disabled:bg-stone-50 disabled:text-stone-400';

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={`rounded px-1 py-0.5 text-xs leading-none hover:bg-stone-100 disabled:opacity-30 ${
        danger ? 'text-stone-400 hover:text-red-600' : 'text-stone-500'
      }`}
    >
      {children}
    </button>
  );
}

function MoveButtons({
  onUp,
  onDown,
  disabled,
  upDisabled,
  downDisabled,
}: {
  onUp: () => void;
  onDown: () => void;
  disabled?: boolean;
  upDisabled?: boolean;
  downDisabled?: boolean;
}) {
  return (
    <>
      <IconButton label="Flytta upp" onClick={onUp} disabled={disabled || upDisabled}>
        ↑
      </IconButton>
      <IconButton label="Flytta ner" onClick={onDown} disabled={disabled || downDisabled}>
        ↓
      </IconButton>
    </>
  );
}

// ── Component (key-value rows or free text) ───────────────────────────────

function KeyValueRows({
  rows,
  disabled,
  onChange,
}: {
  rows: ReadonlyArray<{ key: string; value: string }>;
  disabled: boolean;
  onChange: (rows: { key: string; value: string }[]) => void;
}) {
  const set = (i: number, patch: Partial<{ key: string; value: string }>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="flex flex-col gap-1">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            type="text"
            value={r.key}
            placeholder="Etikett"
            disabled={disabled}
            onChange={(e) => set(i, { key: e.target.value })}
            className={`${inputCls} basis-2/5`}
          />
          <input
            type="text"
            value={r.value}
            placeholder="Värde"
            disabled={disabled}
            onChange={(e) => set(i, { value: e.target.value })}
            className={`${inputCls} basis-3/5`}
          />
          <IconButton
            label="Ta bort rad"
            danger
            disabled={disabled}
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            ✕
          </IconButton>
        </div>
      ))}
      <IconButton
        label="Lägg till rad"
        disabled={disabled || rows.length >= MAX_CUSTOM_ROWS}
        onClick={() => onChange([...rows, { key: '', value: '' }])}
      >
        + rad
      </IconButton>
    </div>
  );
}

function ComponentCard({
  component,
  disabled,
  upDisabled,
  downDisabled,
  onChange,
  onMove,
  onRemove,
}: {
  component: CustomComponent;
  disabled: boolean;
  upDisabled: boolean;
  downDisabled: boolean;
  onChange: (next: CustomComponent) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex flex-col gap-1 rounded border border-stone-200 bg-white p-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wide text-stone-400">
          {component.kind === 'text' ? 'Fritext' : 'Nyckel–värde'}
        </span>
        <div className="flex items-center gap-0.5">
          <MoveButtons
            disabled={disabled}
            upDisabled={upDisabled}
            downDisabled={downDisabled}
            onUp={() => onMove(-1)}
            onDown={() => onMove(1)}
          />
          <IconButton label="Ta bort" danger disabled={disabled} onClick={onRemove}>
            ✕
          </IconButton>
        </div>
      </div>
      {component.kind === 'text' ? (
        <textarea
          value={component.body}
          placeholder="Skriv text…"
          disabled={disabled}
          rows={3}
          onChange={(e) => onChange({ ...component, body: e.target.value })}
          className={inputCls}
        />
      ) : (
        <KeyValueRows
          rows={component.rows}
          disabled={disabled}
          onChange={(rows) => onChange({ ...component, rows })}
        />
      )}
    </div>
  );
}

// ── SubSection ────────────────────────────────────────────────────────────

function SubSectionCard({
  subSection,
  label,
  disabled,
  upDisabled,
  downDisabled,
  onChange,
  onMove,
  onRemove,
}: {
  subSection: CustomSubSection;
  /** "§N.M" when named, else null (blend). */
  label: string | null;
  disabled: boolean;
  upDisabled: boolean;
  downDisabled: boolean;
  onChange: (next: CustomSubSection) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const comps = subSection.components;
  const setComp = (i: number, next: CustomComponent) =>
    onChange({ ...subSection, components: comps.map((c, j) => (j === i ? next : c)) });
  const addComp = (kind: CustomComponent['kind']) => {
    if (comps.length >= MAX_CUSTOM_COMPONENTS) return;
    const base: CustomComponent =
      kind === 'text'
        ? { id: newId(), kind: 'text', body: '' }
        : { id: newId(), kind: 'key_value', rows: [{ key: '', value: '' }] };
    onChange({ ...subSection, components: [...comps, base] });
  };
  return (
    <div className="flex flex-col gap-2 rounded border border-stone-200 bg-stone-50 p-2">
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-[11px] font-medium text-stone-400">
          {label ?? 'del'}
        </span>
        <input
          type="text"
          value={subSection.title ?? ''}
          placeholder="Namn (valfritt — annars del av sektionen)"
          disabled={disabled}
          onChange={(e) =>
            onChange({ ...subSection, title: e.target.value || undefined })
          }
          className={inputCls}
        />
        <div className="flex shrink-0 items-center gap-0.5">
          <MoveButtons
            disabled={disabled}
            upDisabled={upDisabled}
            downDisabled={downDisabled}
            onUp={() => onMove(-1)}
            onDown={() => onMove(1)}
          />
          <IconButton label="Ta bort underavsnitt" danger disabled={disabled} onClick={onRemove}>
            ✕
          </IconButton>
        </div>
      </div>

      {comps.map((c, i) => (
        <ComponentCard
          key={c.id}
          component={c}
          disabled={disabled}
          upDisabled={i === 0}
          downDisabled={i === comps.length - 1}
          onChange={(next) => setComp(i, next)}
          onMove={(dir) => onChange({ ...subSection, components: move(comps, i, dir) })}
          onRemove={() =>
            onChange({ ...subSection, components: comps.filter((_, j) => j !== i) })
          }
        />
      ))}

      <div className="flex gap-2">
        <IconButton
          label="Lägg till nyckel–värde"
          disabled={disabled || comps.length >= MAX_CUSTOM_COMPONENTS}
          onClick={() => addComp('key_value')}
        >
          + Nyckel–värde
        </IconButton>
        <IconButton
          label="Lägg till fritext"
          disabled={disabled || comps.length >= MAX_CUSTOM_COMPONENTS}
          onClick={() => addComp('text')}
        >
          + Fritext
        </IconButton>
      </div>
    </div>
  );
}

// ── Section ─────────────────────────────────────────────────────────────

function SectionCard({
  section,
  number,
  placementOptions,
  disabled,
  upDisabled,
  downDisabled,
  onChange,
  onMove,
  onRemove,
}: {
  section: CustomSection;
  number: number | null;
  placementOptions: ReadonlyArray<{ ref: string; label: string }>;
  disabled: boolean;
  upDisabled: boolean;
  downDisabled: boolean;
  onChange: (next: CustomSection) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const subs = section.subSections;
  const setSub = (i: number, next: CustomSubSection) =>
    onChange({ ...section, subSections: subs.map((s, j) => (j === i ? next : s)) });
  const addSub = () => {
    if (subs.length >= MAX_CUSTOM_SUBSECTIONS) return;
    onChange({
      ...section,
      subSections: [...subs, { id: newId(), components: [] }],
    });
  };

  // Local §N.M numbering: count only named sub-sections, in order.
  let namedSeen = 0;

  return (
    <fieldset className="flex flex-col gap-2 rounded border border-dashed border-stone-300 p-3">
      <legend className="flex items-center gap-2 px-1 text-xs font-medium uppercase tracking-wider text-stone-500">
        <span className="text-stone-400">{number != null ? `§${number}` : 'Egen sektion'}</span>
        <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[10px] tracking-normal text-stone-500">
          egen
        </span>
      </legend>

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={section.title}
          placeholder="Rubrik (t.ex. Särskild överenskommelse)"
          disabled={disabled}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          className={inputCls}
        />
        <div className="flex shrink-0 items-center gap-0.5">
          <MoveButtons
            disabled={disabled}
            upDisabled={upDisabled}
            downDisabled={downDisabled}
            onUp={() => onMove(-1)}
            onDown={() => onMove(1)}
          />
          <IconButton label="Ta bort sektion" danger disabled={disabled} onClick={onRemove}>
            ✕
          </IconButton>
        </div>
      </div>

      {placementOptions.length > 0 && (
        <label className="flex items-center gap-2 text-[11px] text-stone-500">
          Placera efter
          <select
            value={section.afterRef ?? ''}
            disabled={disabled}
            onChange={(e) =>
              onChange({ ...section, afterRef: e.target.value || undefined })
            }
            className="rounded border border-stone-300 px-1.5 py-0.5 text-xs"
          >
            {placementOptions.map((o) => (
              <option key={o.ref} value={o.ref}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {subs.map((ss, i) => {
        const named = (ss.title ?? '').trim() !== '';
        const label =
          named && number != null ? `§${number}.${(namedSeen += 1)}` : null;
        if (named && number == null) namedSeen += 1;
        return (
          <SubSectionCard
            key={ss.id}
            subSection={ss}
            label={label}
            disabled={disabled}
            upDisabled={i === 0}
            downDisabled={i === subs.length - 1}
            onChange={(next) => setSub(i, next)}
            onMove={(dir) => onChange({ ...section, subSections: move(subs, i, dir) })}
            onRemove={() =>
              onChange({ ...section, subSections: subs.filter((_, j) => j !== i) })
            }
          />
        );
      })}

      <IconButton
        label="Lägg till underavsnitt"
        disabled={disabled || subs.length >= MAX_CUSTOM_SUBSECTIONS}
        onClick={addSub}
      >
        + Underavsnitt
      </IconButton>
    </fieldset>
  );
}

// ── Top-level editor ──────────────────────────────────────────────────────

export function CustomSectionsEditor({
  sections,
  numbering,
  placementOptions,
  defaultAfterRef,
  disabled,
  onChange,
}: {
  sections: ReadonlyArray<CustomSection>;
  numbering: SectionNumbering | null;
  /** Declared sections a custom section can be placed after (visible only). */
  placementOptions: ReadonlyArray<{ ref: string; label: string }>;
  /** afterRef seeded on a freshly-added section. */
  defaultAfterRef: string | undefined;
  disabled: boolean;
  onChange: (next: CustomSection[]) => void;
}) {
  const add = () => {
    if (sections.length >= MAX_CUSTOM_SECTIONS) return;
    onChange([
      ...sections,
      {
        id: newId(),
        title: '',
        afterRef: defaultAfterRef,
        subSections: [{ id: newId(), components: [{ id: newId(), kind: 'key_value', rows: [{ key: '', value: '' }] }] }],
      },
    ]);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="custom-sections-editor">
      {sections.map((cs, i) => {
        const n = numbering?.get(customSectionAnchorRef(cs.id));
        return (
          <SectionCard
            key={cs.id}
            section={cs}
            number={typeof n === 'number' ? n : null}
            placementOptions={placementOptions}
            disabled={disabled}
            upDisabled={i === 0}
            downDisabled={i === sections.length - 1}
            onChange={(next) => onChange(sections.map((s, j) => (j === i ? next : s)))}
            onMove={(dir) => onChange(move(sections, i, dir))}
            onRemove={() => onChange(sections.filter((_, j) => j !== i))}
          />
        );
      })}

      <button
        type="button"
        disabled={disabled || sections.length >= MAX_CUSTOM_SECTIONS}
        onClick={add}
        className="self-start rounded border border-dashed border-stone-400 px-3 py-1.5 text-xs font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-40"
      >
        + Lägg till egen sektion
      </button>
    </div>
  );
}
